import { useState } from "react";

import {
  useFacets,
  useQuestions,
  useRetryFailed,
  useStatusCounts,
  useSubmitJob,
  type QuestionFilters,
  type RetryFailedResult,
} from "@/api/queries";
import { QuestionDetail } from "@/components/question-detail";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Combobox, FieldLabel, Input } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, Panel } from "@/components/ui/panel";
import { cn, formatScore } from "@/lib/utils";

/** DESIGN §14.2 — the whole bank, filtered. Server-side paging: §3 makes every query a
 *  network round trip, so fetching 50k rows to filter them in the browser is not an
 *  option that gets better later. */
const PAGE = 50;

const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";

export default function Questions() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string[]>([]);
  const [stack, setStack] = useState("");
  const [concept, setConcept] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Set once retry-failed responds — this screen's equivalent of Review's PipelineNudge,
  // just summarising a sweep instead of one row's new stage (§10.2 has no single stage).
  const [retryResult, setRetryResult] = useState<RetryFailedResult | null>(null);

  const filters: QuestionFilters = {
    limit: PAGE,
    offset,
    ...(search ? { search } : {}),
    ...(status.length > 0 ? { status } : {}),
    ...(stack ? { stack_path: stack } : {}),
    ...(concept ? { primary_concept: concept } : {}),
  };
  const { data, isLoading, error, isPlaceholderData } = useQuestions(filters);
  const { data: counts } = useStatusCounts();
  const { data: facets } = useFacets();
  const retryFailed = useRetryFailed();
  const submitJob = useSubmitJob();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const failedCount = counts?.counts?.failed ?? 0;

  function toggleStatus(value: string) {
    setOffset(0);
    setStatus((current) =>
      current.includes(value) ? current.filter((s) => s !== value) : [...current, value],
    );
  }

  function doRetryFailed() {
    retryFailed.mutate(undefined, { onSuccess: setRetryResult });
  }

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <header className="flex items-center gap-4">
        <h1 className="text-base font-semibold tracking-tight">Questions</h1>
        <span className="text-bank-muted font-mono text-xs tabular-nums">
          {isLoading ? "loading…" : `${total} live`}
        </span>
      </header>

      <Panel className="p-3">
        {/* Every filter resets the offset: page 7 of the old result set is a different
            set of rows once the filter changes, and landing there looks like data loss. */}
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
          <div>
            <FieldLabel htmlFor="q-search">Text contains</FieldLabel>
            <Input
              id="q-search"
              value={search}
              placeholder="closure, middleware, LEFT JOIN…"
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-stack">Stack</FieldLabel>
            <Combobox
              id="q-stack"
              value={stack}
              onChange={(v) => {
                setStack(v);
                setOffset(0);
              }}
              options={[
                { value: "", label: "any" },
                ...(facets?.stack_paths ?? []).map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>
          <div>
            <FieldLabel htmlFor="q-concept">Concept</FieldLabel>
            <Combobox
              id="q-concept"
              value={concept}
              onChange={(v) => {
                setConcept(v);
                setOffset(0);
              }}
              options={[
                { value: "", label: "any" },
                ...(facets?.primary_concepts ?? []).map((c) => ({ value: c, label: c })),
              ]}
            />
          </div>
        </div>
        {/* The facet carries the status dot rather than a whole Badge: a chip inside a
            button gives two pressed states arguing about which one you clicked. */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {Object.entries(counts?.counts ?? {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([name, n]) => {
              const on = status.includes(name);
              return (
                <button
                  key={name}
                  onClick={() => toggleStatus(name)}
                  aria-pressed={on}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-[11px] transition-colors",
                    "focus-visible:ring-bank-accent focus-visible:ring-2 focus-visible:outline-none",
                    on
                      ? "border-bank-accent bg-bank-accent-wash text-bank-accent-ink"
                      : "border-bank-border text-bank-muted hover:bg-bank-surface-2",
                  )}
                >
                  <StatusDot value={name} />
                  {name}
                  <span className={cn("font-medium tabular-nums", !on && "text-bank-ink")}>{n}</span>
                </button>
              );
            })}
          {failedCount > 0 && (
            <Button
              size="sm"
              disabled={retryFailed.isPending}
              title="Reset failed questions and chunks below max_retries to the status they failed from (§10.2)"
              onClick={doRetryFailed}
              className="ml-1"
            >
              {retryFailed.isPending ? "Retrying…" : `Retry ${failedCount} failed`}
            </Button>
          )}
        </div>
      </Panel>

      <ErrorNote error={error ?? retryFailed.error} />

      {retryResult && (
        <Callout tone="attention" className="flex flex-wrap items-center justify-between gap-2">
          <span>
            {retryResult.questions} question{retryResult.questions === 1 ? "" : "s"}
            {retryResult.chunks > 0 ? ` and ${retryResult.chunks} chunk(s)` : ""} reset to retry
            {retryResult.exhausted > 0 ? `; ${retryResult.exhausted} past max_retries, left failed` : ""}.
            Returns here after the next pipeline run.
          </span>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={submitJob.isPending}
              onClick={() => submitJob.mutate("pipeline", { onSuccess: () => setRetryResult(null) })}
            >
              {submitJob.isPending ? "Queuing…" : "Run pipeline"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRetryResult(null)}>
              Dismiss
            </Button>
          </div>
        </Callout>
      )}

      <Panel className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-auto">
          {items.length === 0 && !isLoading ? (
            <Empty>Nothing matches.</Empty>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-bank-surface-2 border-bank-border sticky top-0 border-b text-left">
                <tr>
                  <th className={TH}>#</th>
                  <th className={TH}>status</th>
                  <th className={TH}>question</th>
                  <th className={TH}>stack / concept</th>
                  <th className={TH}>type</th>
                  <th className={cn(TH, "text-right")}>diff</th>
                  <th className={cn(TH, "text-right")}>quality</th>
                  <th className={TH}>cluster</th>
                </tr>
              </thead>
              <tbody className={isPlaceholderData ? "opacity-60" : undefined}>
                {items.map((q) => (
                  <tr
                    key={q.id}
                    onClick={() => setSelectedId(q.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedId(q.id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`View question ${q.id} and its history`}
                    className="border-bank-border hover:bg-bank-surface-2 focus-visible:outline-bank-accent cursor-pointer border-t transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2"
                  >
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px] tabular-nums">{q.id}</td>
                    <td className="px-3 py-2">
                      <Badge value={q.status} />
                    </td>
                    <td className="question-text max-w-xl px-3 py-2 text-[13px]">{q.question_text}</td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px] leading-relaxed">
                      {q.stack_path ?? "—"}
                      <br />
                      {q.primary_concept ?? "—"}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {q.question_type ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">
                      {q.difficulty_score ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] tabular-nums">
                      {formatScore(q.quality_score)}
                    </td>
                    <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                      {q.canonical_id
                        ? `→ #${q.canonical_id}`
                        : (q.cluster_size ?? 1) > 1
                          ? "canonical"
                          : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="border-bank-border flex items-center justify-between border-t px-3 py-2">
          <p className="text-bank-muted font-mono text-[11px] tabular-nums">
            {total === 0 ? "0" : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}
          </p>
          <div className="flex gap-1.5">
            <Button size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Previous
            </Button>
            <Button size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
              Next
            </Button>
          </div>
        </div>
      </Panel>

      <Drawer
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title={selectedId !== null ? `Question #${selectedId}` : ""}
      >
        {selectedId !== null && (
          <QuestionDetail id={selectedId} showStatusActions onOpenQuestion={setSelectedId} />
        )}
      </Drawer>
    </div>
  );
}
